import { ITableConfigValueColumn } from '../../models/tableConfig.ts';
import { formatDatetime } from '../../utils/formatDate.ts';
import { ApplicationLink } from '../ApplicationLink';
import { StatusTag } from '../StatusTag';
import {
  ace_index,
  loan_quote,
  loan_request,
} from '@axo/shared/data-access/types';
import {
  useQuoteStatusUtils,
  useRequestStatusUtils,
} from '@axo/shared/data-access/hooks';
import { LenderName } from '../SearchResults/LenderName';
import { UserName } from '../UserName';
import { DisplayColumn, SearchResultModel } from './SearchResultModel.ts';

function acceptBy(code: ace_index.AcceptedByCode) {
  switch (code) {
    case ace_index.AcceptedByCode.MyPage:
      return 'Customer (MyPage)';
    case ace_index.AcceptedByCode.AutoAccept:
      return 'AutoAccept';
    case ace_index.AcceptedByCode.ACE:
      return 'ACE';
    default:
      return 'Unknown source';
  }
}

export function searchResultModelMapper(
  quoteStatusUtils: ReturnType<typeof useQuoteStatusUtils>,
  requestStatusUtils: ReturnType<typeof useRequestStatusUtils>,
) {
  return function (
    loanApplication: ace_index.LoanApplication,
  ): SearchResultModel {
    return {
      LoanApplicationID: loanApplication.ID,
      HumanReadableID: loanApplication.HumanReadableID,
      MarketCountry: loanApplication.MarketCountryCode,
      Status: loanApplication.Status?.Name,
      QuoteStatuses: loanApplication.LoanQuotes
        ? new Set(
            loanApplication.LoanQuotes.map(({ StatusCode }) => StatusCode)
              .filter((status): status is number => !!status)
              .map(quoteStatusUtils.fromCode)
              .filter(
                (status): status is loan_quote.LoanQuoteStatus => !!status,
              ),
          )
        : undefined,
      RequestStatuses: loanApplication.LoanRequests
        ? new Set(
            loanApplication.LoanRequests.map(({ StatusCode }) => StatusCode)
              .filter((code): code is number => !!code)
              .map(requestStatusUtils.fromCode)
              .filter(
                (status): status is loan_request.LoanRequestStatus => !!status,
              ),
          )
        : undefined,
      LastMyPageLogin: loanApplication.LoanApplicationSummary?.LastMyPageLoginAt
        ? new Date(loanApplication.LoanApplicationSummary.LastMyPageLoginAt)
        : undefined,
      LastAcceptBy: loanApplication.LoanApplicationSummary?.LastAcceptByCode
        ? acceptBy(loanApplication.LoanApplicationSummary.LastAcceptByCode)
        : undefined,
      NumCalls: loanApplication.LoanApplicationSummary?.NumCalls,
      NumAnsweredCalls:
        loanApplication.LoanApplicationSummary?.NumAnsweredCalls,
      NumNotAnsweredCalls:
        loanApplication.LoanApplicationSummary?.NumNotAnsweredCalls,
      LastCallAt: loanApplication.LoanApplicationSummary?.LastCallAt
        ? new Date(loanApplication.LoanApplicationSummary.LastCallAt)
        : undefined,
      ApprovedByLenderIDs: loanApplication.LoanQuotes
        ? new Set(
            loanApplication.LoanQuotes?.filter((quote) =>
              quoteStatusUtils.isInQuoteStatusGroup(
                quoteStatusUtils.fromCode(quote.StatusCode),
                loan_quote.QuoteStatusGroup.Approved,
              ),
            ).map((quote) => quote.LenderID),
          )
        : undefined,
      LastAcceptedAmount:
        loanApplication.LoanApplicationSummary?.LastAcceptedAmount,
      HighestApprovedAmount:
        loanApplication.LoanApplicationSummary?.HighestApprovedAmount,
      FirstApprovedOfferAt: loanApplication.LoanApplicationSummary
        ?.FirstApprovedOfferAt
        ? new Date(loanApplication.LoanApplicationSummary.FirstApprovedOfferAt)
        : undefined,
      FirstAcceptedOfferAt: loanApplication.LoanApplicationSummary
        ?.FirstAcceptedOfferAt
        ? new Date(loanApplication.LoanApplicationSummary.FirstAcceptedOfferAt)
        : undefined,
      LastAcceptedOfferAt: loanApplication.LoanApplicationSummary
        ?.LastAcceptedOfferAt
        ? new Date(loanApplication.LoanApplicationSummary.LastAcceptedOfferAt)
        : undefined,
      LastAcceptedLenderID:
        loanApplication.LoanApplicationSummary?.LastAcceptedLenderID,
      LastLoanRequestAmount:
        loanApplication.LoanApplicationSummary?.LastLoanRequestAmount,

      NumRegisteredLoanRequests:
        loanApplication.LoanApplicationSummary?.NumRegisteredLoanRequests,
      NumAnsweredLoanRequests:
        loanApplication.LoanApplicationSummary?.NumAnsweredLoanRequests,

      CompletedAt: loanApplication.CompletedAt,
      AppliedAmount: loanApplication.Amount,
      UpdatedAt: loanApplication.UpdatedAt,

      FirstName: loanApplication.Person?.FirstName,
      LastName: loanApplication.Person?.LastName,
      SSN: loanApplication.Person?.SocialSecurityNumber,

      Phone: loanApplication.Customer?.MobilePhoneNumber,
      Email: loanApplication.Customer?.Email,

      LockedBy: loanApplication.Lock?.LockedByID,

      Owner: loanApplication.Assignment?.UserID,

      ApplicantRole: loanApplication.Channels?.find((x) => !!x.InternalSource)
        ?.InternalIdentityRoleName?.Name,

      NumApprovedQuotes:
        loanApplication.LoanApplicationSummary?.NumApprovedQuotes,

      Product: loanApplication.Product,
    };
  };
}

type SearchDisplayColumnSpecification = {
  label: string;
  render: ITableConfigValueColumn<SearchResultModel>['render'];
};

export const searchDiplayColumns: {
  [Property in DisplayColumn]: SearchDisplayColumnSpecification & {
    mandatory?: boolean;
  };
} = {
  HumanReadableID: {
    label: 'Application Number',
    render: (value, item) => (
      <ApplicationLink
        id={item.LoanApplicationID}
        humanReadableID={value as string}
        marketCountry={item.MarketCountry}
      />
    ),
    mandatory: true,
  },
  Status: {
    label: 'Status',
    render: (value) => (
      <StatusTag status={value?.toString() ?? ''}>
        {value?.toString() ?? ''}
      </StatusTag>
    ),
  },
  QuoteStatuses: {
    label: 'Quote Statuses',
    render: (_, item) => (
      <>
        {item.QuoteStatuses
          ? Array.from(item.QuoteStatuses).map((status) => (
              <StatusTag key={status} status={status}>
                {status}
              </StatusTag>
            ))
          : null}
      </>
    ),
  },
  RequestStatuses: {
    label: 'Request Statuses',
    render: (_, item) => (
      <>
        {item.RequestStatuses
          ? Array.from(item.RequestStatuses).map((status) => (
              <StatusTag key={status} status={status}>
                {status}
              </StatusTag>
            ))
          : null}
      </>
    ),
  },
  LastMyPageLogin: {
    label: 'Last MyPage Login at',
    render: (_, item) =>
      formatDatetime(item.LastMyPageLogin?.toISOString()) ?? '',
  },
  LastAcceptBy: {
    label: 'Last Accept by',
    render: (value) => value?.toLocaleString() ?? '',
  },
  NumCalls: {
    label: '# Calls',
    render: (value) => value?.toLocaleString() ?? '',
  },
  NumAnsweredCalls: {
    label: '# Answered',
    render: (value) => value?.toLocaleString() ?? '',
  },
  NumNotAnsweredCalls: {
    label: '# Unanswered',
    render: (value) => value?.toLocaleString() ?? '',
  },
  LastCallAt: {
    label: 'Last Call Attempt',
    render: (_, item) => formatDatetime(item.LastCallAt?.toISOString()) ?? '',
  },
  LastAcceptedLenderID: {
    label: 'Accepted bank (latest accepted)',
    render: (_, item) =>
      item.LastAcceptedLenderID ? (
        <LenderName
          marketCountry={item.MarketCountry}
          lenderID={item.LastAcceptedLenderID}
        />
      ) : (
        ''
      ),
  },
  ApprovedByLenderIDs: {
    label: 'Approved by',
    render: (_, item) =>
      item.ApprovedByLenderIDs ? (
        <span>
          {Array.from(item.ApprovedByLenderIDs).map((lenderID) => (
            <LenderName
              marketCountry={item.MarketCountry}
              lenderID={lenderID}
              key={lenderID}
            />
          ))}
        </span>
      ) : (
        ''
      ),
  },
  LastAcceptedAmount: {
    label: 'Accepted Amount',
    render: (value) => value?.toString() ?? '',
  },
  HighestApprovedAmount: {
    label: 'Highest Approved Amount',
    render: (value) => value?.toString() ?? '',
  },
  FirstApprovedOfferAt: {
    label: 'Approved since',
    render: (_, { FirstApprovedOfferAt: value }) =>
      value?.toLocaleString() ?? '',
  },
  FirstAcceptedOfferAt: {
    label: 'Accepted since',
    render: (_, { FirstAcceptedOfferAt: value }) =>
      value?.toLocaleString() ?? '',
  },
  LastAcceptedOfferAt: {
    label: 'Accepted at (latest offer accepted)',
    render: (_, { LastAcceptedOfferAt: value }) =>
      value?.toLocaleString() ?? '',
  },
  LastLoanRequestAmount: {
    label: 'Applied amount (from latest loan request)',
    render(value) {
      return value?.toString() ?? '';
    },
  },
  NumRegisteredLoanRequests: {
    label: 'No. sent requests',
    render(value) {
      return value?.toString() ?? '';
    },
  },
  NumAnsweredLoanRequests: {
    label: 'No. answered requests',
    render(value) {
      return value?.toString() ?? '';
    },
  },
  CompletedAt: {
    label: 'Application Completed',
    render: (value) => formatDatetime(value?.toLocaleString()) ?? '',
  },
  FirstName: {
    label: 'First Name',
    render: (value) => value?.toString() ?? '',
  },
  AppliedAmount: {
    label: 'Loan Amount (requested)',
    render: (value) => value?.toString() ?? '',
  },
  LastName: {
    label: 'Last Name',
    render: (value) => value?.toString() ?? '',
  },
  Phone: {
    label: 'Phone',
    render: (value) => value?.toString() ?? '',
  },
  Email: {
    label: 'Email',
    render: (value) => value?.toString() ?? '',
  },
  SSN: {
    label: 'SSN',
    render: (value) => value?.toString() ?? '',
  },
  LockedBy: {
    label: 'Locked by',
    render: (value) => <UserName clientID={value?.toString() ?? ''} />,
  },
  Owner: {
    label: 'Owner',
    render: (value) => <UserName clientID={value?.toString() ?? ''} />,
  },
  UpdatedAt: {
    label: 'Last updated',
    render: (_, item) => formatDatetime(item.UpdatedAt?.toLocaleString()),
  },
  ApplicantRole: {
    label: 'Applicant Role',
    render: (_, item) => item.ApplicantRole?.toString() ?? '',
  },
  NumApprovedQuotes: {
    label: 'No. Approved Quotes',
    render: (value) => value?.toString() ?? '',
  },
  Product: {
    label: 'Product',
    render: (_, item) => item.Product?.Name ?? '',
  },
};
